// @flow
/**
 * Native WebSocket client for the admin live schedule feed.
 * No socket.io — reconnect + subscribe handled here.
 *
 * Server: wss?://{apiHost}/ws/schedules/
 * (api host is REACT_APP_SERVER_URL without /api/v1)
 */

type Selection = {
  programId: number,
  date: string,
  scheduleTypeId: number,
};

type MessageHandler = (msg: Object) => void;

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;
const PING_INTERVAL_MS = 20000;
const SAFETY_POLL_MS = 60000;

function buildWsUrl(): string {
  const apiBase = process.env.REACT_APP_SERVER_URL || '';
  // REACT_APP_SERVER_URL is typically http://host:8000/api/v1
  let origin = apiBase.replace(/\/api\/v1\/?$/, '');
  if (!origin) {
    if (typeof window !== 'undefined') {
      origin = window.location.origin;
    } else {
      origin = 'http://127.0.0.1:8000';
    }
  }
  const wsOrigin = origin.replace(/^http/, 'ws');
  return `${wsOrigin}/ws/schedules/`;
}

class ScheduleSocketManager {
  ws: ?WebSocket;
  sockets: Set<WebSocket>;
  handlers: Set<MessageHandler>;
  selection: ?Selection;
  intentionalClose: boolean;
  reconnectAttempt: number;
  reconnectTimer: ?TimeoutID;
  pingTimer: ?IntervalID;
  status: 'closed' | 'connecting' | 'open' | 'error';
  statusListeners: Set<(s: string) => void>;
  _pendingReconnect: boolean;
  _reconnectFlight: ?Promise<any>;
  _reconnectFlightKey: ?string;

  constructor() {
    this.ws = null;
    this.sockets = new Set();
    this.handlers = new Set();
    this.selection = null;
    this.intentionalClose = false;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.status = 'closed';
    this.statusListeners = new Set();
    // Called after unintentional reconnect + resubscribe (board resync)
    this.onReconnectHandlers = new Set();
    this._pendingReconnect = false;
    this._reconnectFlight = null;
    this._reconnectFlightKey = null;
  }

  onReconnect(handler: (selection: ?Selection) => any) {
    this.onReconnectHandlers.add(handler);
    return () => this.onReconnectHandlers.delete(handler);
  }

  _notifyReconnect() {
    const selection = this.selection;
    const key = selection
      ? `${selection.programId}|${selection.date}|${selection.scheduleTypeId}`
      : '';
    if (this._reconnectFlight && this._reconnectFlightKey === key) {
      return this._reconnectFlight;
    }
    const handlers = Array.from(this.onReconnectHandlers);
    let chain = Promise.resolve();
    handlers.forEach((fn) => {
      chain = chain.then(() => fn(selection));
    });
    this._reconnectFlightKey = key;
    this._reconnectFlight = chain.finally(() => {
      if (this._reconnectFlightKey === key) {
        this._reconnectFlight = null;
        this._reconnectFlightKey = null;
      }
    });
    return this._reconnectFlight;
  }

  getStatus() {
    return this.status;
  }

  onStatus(listener: (s: string) => void) {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  _setStatus(status: string) {
    this.status = status;
    this.statusListeners.forEach((fn) => {
      try {
        fn(status);
      } catch (e) {
        // ignore
      }
    });
  }

  onMessage(handler: MessageHandler) {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  connect() {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.intentionalClose = false;
    this._setStatus('connecting');
    const url = buildWsUrl();
    let ws;
    try {
      // Browser sends cookies for same-site / configured domain automatically
      ws = new WebSocket(url);
    } catch (e) {
      this._setStatus('error');
      this._scheduleReconnect();
      return;
    }
    this.sockets.add(ws);
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) {
        try {
          ws.close();
        } catch (e) {
          // ignore
        }
        return;
      }
      const wasReconnect = this._pendingReconnect;
      this._pendingReconnect = false;
      this.reconnectAttempt = 0;
      this._setStatus('open');
      this._startPing();
      if (this.selection) {
        this._sendSubscribe(this.selection);
      }
      if (wasReconnect) {
        this._notifyReconnect();
      }
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      this.handlers.forEach((h) => {
        try {
          h(msg);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('scheduleSocket handler error', err);
        }
      });
    };

    ws.onerror = () => {
      if (this.ws !== ws) {
        return;
      }
      this._setStatus('error');
    };

    ws.onclose = () => {
      this.sockets.delete(ws);
      if (this.ws !== ws) {
        return;
      }
      this._stopPing();
      this.ws = null;
      if (!this.intentionalClose) {
        this._pendingReconnect = true;
        this._setStatus('closed');
        this._scheduleReconnect();
      } else {
        this._setStatus('closed');
      }
    };
  }

  disconnect() {
    this.intentionalClose = true;
    this._pendingReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._stopPing();
    if (this.selection && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ type: 'unsubscribe', ...this.selection });
    }
    this.selection = null;
    const sockets = Array.from(this.sockets);
    this.sockets.clear();
    this.ws = null;
    sockets.forEach((sock) => {
      try {
        sock.close();
      } catch (e) {
        // ignore
      }
    });
    this._setStatus('closed');
  }

  subscribe(selection: Selection) {
    this.selection = selection;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this._sendSubscribe(selection);
    } else {
      this.connect();
    }
  }

  unsubscribe() {
    if (this.selection) {
      this.send({ type: 'unsubscribe', ...this.selection });
    }
    this.selection = null;
  }

  acquireLock(participantUserId: number, extra?: Object = {}) {
    const sel = this.selection || {};
    this.send({
      type: 'lock.acquire',
      participantUserId,
      date: extra.date || sel.date,
      scheduleTypeId: extra.scheduleTypeId || sel.scheduleTypeId,
      programId: extra.programId || sel.programId,
    });
  }

  /**
   * Request a soft lock and resolve with granted (true/false).
   *
   * Fail-OPEN when the socket is not open (solo admin must still schedule).
   * Explicit lock.denied → false.
   * Timeout without response → true (fail-open; server may be slow / WS blip).
   * Only fail-closed on an explicit deny from the server.
   */
  requestLock(
    participantUserId: number,
    timeoutMs: number = 1500,
    extra?: Object = {}
  ): Promise<boolean> {
    // No live connection — do not block scheduling
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      let settled = false;
      const finish = (ok) => {
        if (settled) return;
        settled = true;
        unsub();
        clearTimeout(timer);
        resolve(ok);
      };
      const unsub = this.onMessage((msg) => {
        if (!msg || msg.participantUserId == null) return;
        if (Number(msg.participantUserId) !== Number(participantUserId)) {
          return;
        }
        if (msg.type === 'lock.granted') {
          finish(true);
        } else if (msg.type === 'lock.denied') {
          finish(false);
        }
      });
      // Timeout without explicit deny → allow mutation (fail-open)
      const timer = setTimeout(() => finish(true), timeoutMs);
      this.acquireLock(participantUserId, extra);
    });
  }

  releaseLock(participantUserId: number, extra?: Object = {}) {
    const sel = this.selection || {};
    this.send({
      type: 'lock.release',
      participantUserId,
      date: extra.date || sel.date,
      scheduleTypeId: extra.scheduleTypeId || sel.scheduleTypeId,
      programId: extra.programId || sel.programId,
    });
  }

  heartbeatLock(participantUserId: number) {
    const sel = this.selection || {};
    this.send({
      type: 'lock.heartbeat',
      participantUserId,
      date: sel.date,
      scheduleTypeId: sel.scheduleTypeId,
    });
  }

  send(payload: Object) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  _sendSubscribe(selection: Selection) {
    this.send({
      type: 'subscribe',
      programId: selection.programId,
      date: selection.date,
      scheduleTypeId: selection.scheduleTypeId,
    });
  }

  _startPing() {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ type: 'presence.ping' });
    }, PING_INTERVAL_MS);
  }

  _stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  _scheduleReconnect() {
    if (this.intentionalClose) {
      return;
    }
    if (this.reconnectTimer) {
      return;
    }
    const cap = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempt),
      RECONNECT_MAX_MS
    );
    // Full jitter inside the cap so a blip does not align every admin.
    const delay = Math.floor(Math.random() * cap);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

export const scheduleSocket = new ScheduleSocketManager();
export { SAFETY_POLL_MS, buildWsUrl, ScheduleSocketManager };
